/**
 * src/config/chartRegistry.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * Single source of truth for every chart in the UI — families, tabs, routing.
 *
 * ARCHITECTURE RULE (Section 1.1 / 1.2 of architecture guide)
 * ─────────────────────────────────────────────────────────────
 * Every chart is INDEPENDENT. `bar-horizontal` and `bar` are as separate as
 * `bar` and `pie`. No shared pages, no variant props, no conditional branching.
 *
 * `family` is a UI-only grouping — it lives here and in HomeChartStudio ONLY.
 * It never appears in ChartConfig, Firestore documents, AnyDataPoint, or any
 * agent-facing shape. The data layer only knows ChartType literals.
 *
 * HOW THE UI WORKS
 * ─────────────────
 * HomeChartStudio calls getActiveFamilies() → renders one primary tab per family.
 * When a family has multiple active charts, ChartFamilyBar renders below the tabs.
 * Each entry is a fully independent chart — clicking it lazy-loads its own page.
 *
 * ADDING A NEW CHART
 * ───────────────────
 * 1. Create src/types/charts/<n>.chart.ts       (types + defaults)
 * 2. Add to chart.registry.ts                    (Steps A–E)
 * 3. Create src/components/charts/<N>Chart.tsx   (renderer)
 * 4. Create src/pages/studio/charts/<N>Page.tsx  (page)
 * 5. Add one ChartEntry object below             ← only change in this file
 *
 * See docs/new_chart_architecture_thought.md for the full checklist.
 */

import type { ChartType } from '../types';
import type { FeatureKey } from './features';
import { FEATURES }        from './features';

// ─────────────────────────────────────────────────────────────────────────────
// FAMILIES
// UI grouping only — never referenced outside this file and HomeChartStudio.
// ─────────────────────────────────────────────────────────────────────────────

export type ChartFamily =
  | 'bar'
  | 'line'
  | 'pie'
  | 'heatmap'
  | 'scatter'
  | 'gauge';

// ─────────────────────────────────────────────────────────────────────────────
// CHART ENTRY
// ─────────────────────────────────────────────────────────────────────────────

export interface ChartEntry {
  /** Must match a value in the ChartType union. */
  chartType:          ChartType;
  /** Shown in the tab bar / ChartFamilyBar sub-selections. */
  label:              string;
  /** UI grouping. One primary tab per family. Never in the data layer. */
  family:             ChartFamily;
  /** Short description — used in tooltips and API docs. */
  description:        string;
  /**
   * Lazy import path for the page component.
   * Resolved by HomeChartStudio via dynamic import.
   */
  pagePath:           string;
  /**
   * True for the chart that loads when the family tab is first clicked.
   * Exactly one entry per family should have isPrimary: true.
   */
  isPrimary?:         boolean;
  /**
   * Feature flag gate. Chart is hidden until the flag is true.
   * Use 'EXTENDED_CHARTS' for all Sprint C charts.
   */
  feature?:           FeatureKey;
  /**
   * In addition to the feature flag, this chart also requires TIMELINE_MODE.
   * Used for bar-race (Data Stream).
   */
  requiresStreaming?:  boolean;
  /** SEO metadata — generates routes, Helmet tags, and sitemap entries. */
  seo?: {
    slug:      string;
    title:     string;
    metaDesc:  string;
    h1:        string;
    bodyText:  string;
    priority?: number;
    features?: { icon: string; title: string; desc: string }[];
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// THE REGISTRY
// Order determines display order within each family's sub-selections.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * All charts. HomeChartStudio reads this at runtime via getActiveFamilies().
 * No other file should contain a hardcoded list of chart types.
 */
export const CHART_REGISTRY: ChartEntry[] = [

  // ── Bar family ─────────────────────────────────────────────────────────────
  {
    chartType:   'bar',
    label:       'Bar',
    family:      'bar',
    description: 'Vertical bars grouped by category. Best for comparing values across discrete groups.',
    pagePath:    './charts/BarChartPage',
    isPrimary:   true,
    seo: {
      slug:     'bar-chart-maker',
      title:    'Bar Chart Maker — Free Animated Bar Chart Video | Chart2Video',
      metaDesc: 'Create animated bar charts and export to MP4 video in seconds. Free online bar graph maker — no signup required.',
      h1:       'Animated Bar Chart Maker',
      bodyText: 'Build animated bar charts and export them as HD video. Perfect for presentations, YouTube, and social media.',
      priority: 1.0,
      features: [
        { icon: '📐', title: '4 Aspect Ratios', desc: '16:9, 9:16, 1:1, and 4:5 canvas' },
        { icon: '🎨', title: 'Per-Bar Styling', desc: 'Color, label, glow per bar' },
        { icon: '🔓', title: 'No Signup Required', desc: 'Start creating immediately' },
      ],
    },
  },
  {
    chartType:   'bar-horizontal',
    label:       'Horizontal',
    family:      'bar',
    description: 'Horizontal bars — ideal for ranked lists and long category names.',
    pagePath:    './charts/BarHorizontalChartPage',
    feature:     'EXTENDED_CHARTS',
    seo: {
      slug:     'horizontal-bar-chart-maker',
      title:    'Horizontal Bar Chart Maker — Free Animated Video | Chart2Video',
      metaDesc: 'Create animated horizontal bar charts. Export to MP4 video. Free horizontal bar graph maker for rankings and comparisons.',
      h1:       'Animated Horizontal Bar Chart Maker',
      bodyText: 'Build animated horizontal bar graphs for rankings, leaderboards, and comparisons with long category labels.',
      priority: 0.8,
      features: [
        { icon: '🏆', title: 'Rank Reveal Animation', desc: 'Countdown from last to first' },
        { icon: '📝', title: 'Long Label Support', desc: 'Category names auto-wrap' },
        { icon: '🔓', title: 'No Signup Required', desc: 'Start creating immediately' },
      ],
    },
  },
  {
    chartType:   'bar-stacked',
    label:       'Stacked',
    family:      'bar',
    description: 'Segments stacked per category to show part-to-whole relationships.',
    pagePath:    './charts/BarStackedChartPage',
    feature:     'EXTENDED_CHARTS',
    seo: {
      slug:     'stacked-bar-chart-maker',
      title:    'Stacked Bar Chart Maker — Free Animated Video | Chart2Video',
      metaDesc: 'Create animated stacked bar charts. Export to MP4 video. Free stacked bar graph maker with 100% normalized mode.',
      h1:       'Animated Stacked Bar Chart Maker',
      bodyText: 'Design animated stacked and segmented bar charts. Show part-to-whole relationships with beautiful layered animations.',
      priority: 0.8,
      features: [
        { icon: '📊', title: '100% Normalize Mode', desc: 'Show proportions per group' },
        { icon: '🎬', title: 'Layer-by-Layer Animation', desc: 'Reveal one series at a time' },
        { icon: '🎨', title: 'Series Color Control', desc: 'One click changes all bars' },
      ],
    },
  },
  {
    chartType:   'bar-grouped',
    label:       'Grouped',
    family:      'bar',
    description: 'Side-by-side bars within each category group for series comparison.',
    pagePath:    './charts/BarGroupedChartPage',
    feature:     'EXTENDED_CHARTS',
    seo: {
      slug:     'grouped-bar-chart-maker',
      title:    'Grouped Bar Chart Maker — Free Animated Video | Chart2Video',
      metaDesc: 'Create animated grouped bar charts online. Compare multiple series side-by-side. Export MP4 video or JPG.',
      h1:       'Animated Grouped Bar Chart Maker',
      bodyText: 'Design animated grouped bar charts to compare multiple data series side-by-side across categories. Perfect for quarterly performance, competitive analysis, and survey results.',
      priority: 0.8,
      features: [
        { icon: '📊', title: 'Side-by-Side Comparison', desc: 'Multiple series per category' },
        { icon: '🎬', title: 'Cascade Animation', desc: 'Series-first dramatic reveal' },
        { icon: '🎨', title: 'Series Color Control', desc: 'One click changes all bars' },
      ],
    },
  },
  {
    chartType:        'bar-race',
    label:            'Bar Race',
    family:           'bar',
    description:      'Animated bar race showing value changes over time.',
    pagePath:         './charts/BarRaceChartPage',
    feature:          'EXTENDED_CHARTS',
    requiresStreaming: true,
  },

  // ── Line family ────────────────────────────────────────────────────────────
  {
    chartType:   'line',
    label:       'Line',
    family:      'line',
    description: 'Connected data points showing trends over time or ordered categories.',
    pagePath:    './charts/LineChartPage',
    isPrimary:   true,
    seo: {
      slug:     'line-chart-maker',
      title:    'Multi-Line Chart Maker — Free Animated Line Graph Video | Chart2Video',
      metaDesc: 'Make animated multi-line charts and line graphs. Compare multiple series like Revenue vs Expenses. Export to MP4 instantly. Free online multi-series line chart creator with area fill.',
      h1:       'Animated Multi-Line Chart Maker',
      bodyText: 'Create animated multi-series line graphs and area charts. Compare trends across multiple data series with per-series styling, dashed lines, and area fills. Export as HD video.',
      priority: 0.9,
      features: [
        { icon: '📊', title: 'Multi-Series Lines', desc: 'Compare multiple data series on one chart' },
        { icon: '📈', title: 'Area Fill Mode', desc: 'Turn any line into an area chart' },
        { icon: '💬', title: 'Per-Series Labels', desc: 'Value, name, pills with custom styling' },
        { icon: '📐', title: '4 Export Formats', desc: 'JPG, MP4, 16:9 through 4:5' },
      ],
    },
  },
  // ── Pie family ─────────────────────────────────────────────────────────────
  {
    chartType:   'pie',
    label:       'Pie',
    family:      'pie',
    description: 'Circular chart showing proportions of a whole.',
    pagePath:    './charts/PieChartPage',
    isPrimary:   true,
    seo: {
      slug:     'pie-chart-maker',
      title:    'Pie Chart Maker — Free Animated Pie Chart Video | Chart2Video',
      metaDesc: 'Create animated pie charts and donut charts online. Export to MP4 instantly. Free pie chart maker with 8 animation styles.',
      h1:       'Animated Pie Chart Maker',
      bodyText: 'Design animated pie charts and donut charts. Show proportions and percentages with beautiful animations.',
      priority: 0.9,
      features: [
        { icon: '🍩', title: 'Donut Mode', desc: 'Adjust inner radius for donut' },
        { icon: '🎭', title: '8 Animation Styles', desc: 'Spin, unfold, wipe, pop & more' },
        { icon: '🎯', title: 'Slice-Level Control', desc: 'Style each slice independently' },
      ],
    },
  },
  // Donut is not a separate chart — it's pie with innerRadius > 0. No separate tab needed.

  // ── Heat Map family ────────────────────────────────────────────────────────
  {
    chartType:   'heatmap',
    label:       'Heat Map',
    family:      'heatmap',
    description: 'Matrix of cells coloured by value — best for two-dimensional data.',
    pagePath:    './charts/HeatMapPage',
    isPrimary:   true,
    seo: {
      slug:     'heatmap-maker',
      title:    'Heatmap Maker — Free Animated Heat Map Video | Chart2Video',
      metaDesc: 'Create animated heat maps online and export to MP4 video. Free heatmap maker with custom color gradients.',
      h1:       'Animated Heatmap Maker',
      bodyText: 'Design animated heat maps for data analysis and presentations. Custom color gradients and smooth reveal animations.',
      priority: 0.8,
      features: [
        { icon: '🌈', title: 'Custom Gradients', desc: 'Multi-stop color scales' },
        { icon: '🔢', title: 'Cell Value Labels', desc: 'Auto, percentage, or integer' },
        { icon: '📊', title: 'Row & Column Headers', desc: 'Fully customizable labels' },
      ],
    },
  },

  // ── Scatter family ─────────────────────────────────────────────────────────
  {
    chartType:   'scatter',
    label:       'Scatter',
    family:      'scatter',
    description: 'X/Y plot showing correlation and distribution.',
    pagePath:    './charts/ScatterChartPage',
    isPrimary:   true,
    feature:     'EXTENDED_CHARTS',
    seo: {
      slug:     'scatter-chart-maker',
      title:    'Scatter Plot Maker — Free Animated Scatter Chart | Chart2Video',
      metaDesc: 'Create animated scatter plots and XY charts. Export to MP4 video. Free scatter chart maker with trend lines.',
      h1:       'Animated Scatter Plot Maker',
      bodyText: 'Build animated scatter plots and correlation charts. Trend line overlays, bubble mode, and quadrant analysis.',
      priority: 0.9,
      features: [
        { icon: '📉', title: 'Trend Line & R²', desc: 'Built-in linear regression' },
        { icon: '🎯', title: 'Quadrant Analysis', desc: 'BCG matrix style dividers' },
        { icon: '🫧', title: 'Bubble Mode', desc: 'Size points by a third variable' },
      ],
    },
  },

  // ── Gauge family ───────────────────────────────────────────────────────────
  {
    chartType:   'gauge',
    label:       'Gauge',
    family:      'gauge',
    description: 'Semi-circle needle gauge — best for a single KPI reading.',
    pagePath:    './charts/GaugeChartPage',
    isPrimary:   true,
    feature:     'EXTENDED_CHARTS',
  },

];

// ─────────────────────────────────────────────────────────────────────────────
// QUERY HELPERS
// HomeChartStudio uses these — never the raw CHART_REGISTRY array directly.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns the de-duplicated list of active families in display order.
 * A family is active if at least one of its charts passes the feature gate.
 */
export function getActiveFamilies(): ChartFamily[] {
  const seen = new Set<ChartFamily>();
  const result: ChartFamily[] = [];
  for (const entry of CHART_REGISTRY) {
    if (isEntryActive(entry) && !seen.has(entry.family)) {
      seen.add(entry.family);
      result.push(entry.family);
    }
  }
  return result;
}

/**
 * Returns the primary chart for a family — the one that loads when the tab
 * is first clicked.
 */
export function getFamilyPrimary(family: ChartFamily): ChartEntry | undefined {
  return CHART_REGISTRY.find(e => e.family === family && e.isPrimary && isEntryActive(e));
}

/**
 * Returns all active charts for a family, in registry order.
 * Used by ChartFamilyBar to render sub-selections.
 */
export function getFamilyCharts(family: ChartFamily): ChartEntry[] {
  return CHART_REGISTRY.filter(e => e.family === family && isEntryActive(e));
}

/**
 * Returns a single registry entry by ChartType and optional pagePath
 * (needed when the same ChartType appears twice, e.g. pie + pie-donut).
 */
export function getChartEntry(chartType: ChartType, pagePath?: string): ChartEntry | undefined {
  return CHART_REGISTRY.find(e =>
    e.chartType === chartType && (!pagePath || e.pagePath === pagePath)
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// INTERNAL HELPERS
// ─────────────────────────────────────────────────────────────────────────────

export function isEntryActive(entry: ChartEntry): boolean {
  if (entry.feature && !FEATURES[entry.feature]) return false;
  if (entry.requiresStreaming && !FEATURES.TIMELINE_MODE) return false;
  return true;
}
